import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import waitForStoreGetter from '@/utils/waitForStoreGetter/waitForStoreGetter';
import logError from './logError/logError';

// NavigationUtils - handles navigation between POV and Angular
// Module state
let router;
let store;
let unsubscribe;

// Navigation utils object containing all navigation-related functions
const navigationUtils = {};

/**
 * Initialize the navigation utility with the Vue router and store
 * @param {Object} importedRouter - The Vue router instance
 * @param {Object} importedStore - The Vuex store instance
 */
navigationUtils.initNavigationUtil = (importedRouter, importedStore) => {
  router = importedRouter;
  store = importedStore;
};

/**
 * Helper function to wait for Angular to load before navigating
 * @param {Object} payload - The navigation payload to use after Angular loads
 * @private
 */
navigationUtils.subscribeToAngularLoad = async (payload) => {
  try {
    await waitForStoreGetter(store, 'AngularStore/isAngularLoaded');
    navigationUtils.handleNavigation(payload);
  } catch (error) {
    logError('Failed while waiting for Angular to load', error, { payload });
  }
};

/**
 * Unified navigation handler that manages routing between POV (Vue) and Angular
 * parts of the application.
 * 
 * @param {Object} payload - Navigation payload
 * @param {string} [payload.name] - Route name for POV navigation (always uses Vue router)
 * @param {string} [payload.path] - Path to navigate to if angular uses deep-link (can lead to Angular or POV)
 *                                 If path starts with '/app/', it's normalized for POV
 * @param {string} [payload.route] - Angular route name (typically starts with 'app.')
 * @param {Object} [payload.params] - Route params for named routes (POV only)
 * @param {Object} [payload.queryParams] - Query parameters to pass in the navigation
 * @returns {void}
 */
navigationUtils.handleNavigation = (payload) => {
  // Validate dependencies
  if (!router) {
    console.error('Router not available for navigation. Make sure initNavigationUtil is called with router.');
    return;
  }
  if (!store) {
    console.error('Store not available for navigation. Make sure initNavigationUtil is called with store.');
    return;
  }

  // Cancel any pending navigation subscription
  if (unsubscribe) unsubscribe();
  
  const { name, path, route, params, queryParams } = payload;
  
  // Always reset modal mode before navigation
  store.dispatch('ModalStore/setForceModalMode', false);
  
  // Case 1: Name-based navigation - always goes to POV
  if (name) {
    router.push({ name, params, query: queryParams });
    store.dispatch('AngularStore/setIsNavigatingToAngular', false);
    return;
  }
  
  // Case 2: Route-based navigation - always goes to Angular
  if (route) {
    // Wait for Angular if needed
    if (!store.getters['AngularStore/isAngularLoaded']) {
      navigationUtils.subscribeToAngularLoad(payload);
      return;
    }
    
    outgoingPostMessageUtils.post('navigate-to', {
      route,
      data: queryParams,
    });
    return;
  }
  
  // Case 3: Path-based navigation - could be Angular or POV
  if (path) {
    // Normalize path by removing Angular prefixes
    const normalizedPath = path.startsWith('/app/') ? path.replace('/app', '') : path;
    
    // Determine if this is an Angular route
    const resolvedRoute = router.resolve(normalizedPath);
    const isAngularRoute = resolvedRoute.route.meta.isAngular;
    
    // Wait for Angular if needed
    if (isAngularRoute && !store.getters['AngularStore/isAngularLoaded']) {
      navigationUtils.subscribeToAngularLoad(payload);
      return;
    }
    
    if (isAngularRoute) {
      outgoingPostMessageUtils.post('deep-link', { path, queryParams });
    } else {
      router.push({ path: normalizedPath, query: queryParams });
      store.dispatch('AngularStore/setIsNavigatingToAngular', false);
    }
  }
};

export const handleNavigation = navigationUtils.handleNavigation;
export const initNavigationUtil = navigationUtils.initNavigationUtil;
